import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

function UserManagement({ siteTimezone, formatDate, formatTime, formatDateTime }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [expandedUser, setExpandedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'admin', 'customer'
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'customer'
  });
  const [roleSettings, setRoleSettings] = useState({
    new_user_default_role: 'customer'
  });
  const [showRolesDialog, setShowRolesDialog] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    }
  };

  // Filter users based on search and role filter
  const filteredUsers = users.filter(user => {
    // Search filter (name or email)
    const matchesSearch = !searchQuery || 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Role filter
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  const startEditingUser = (user) => {
    setEditingUser(user.id);
    setEditValues({
      name: user.name,
      email: user.email,
      role: user.role,
      phone: user.phone || '',
      billing_address: {
        street: user.billing_address?.street || '',
        city: user.billing_address?.city || '',
        state: user.billing_address?.state || '',
        postcode: user.billing_address?.postcode || '',
        country: user.billing_address?.country || 'Australia'
      },
      postal_address: {
        street: user.postal_address?.street || '',
        city: user.postal_address?.city || '',
        state: user.postal_address?.state || '',
        postcode: user.postal_address?.postcode || '',
        country: user.postal_address?.country || 'Australia'
      }
    });
  };

  const copyBillingToShipping = () => {
    if (editValues.billing_address) {
      setEditValues(prev => ({
        ...prev,
        postal_address: { ...prev.billing_address }
      }));
      toast.success('Billing address copied to shipping');
    }
  };

  const handleUpdateUser = async (userId) => {
    setLoading(true);
    try {
      const payload = {
        name: editValues.name,
        email: editValues.email,
        role: editValues.role,
        phone: editValues.phone,
        billing_address: editValues.billing_address,
        postal_address: editValues.postal_address
      };
      
      console.log('Updating user with payload:', payload);
      
      await axios.put(`/admin/users/${userId}`, payload);
      toast.success('✅ User updated successfully');
      setEditingUser(null);
      setEditValues({});
      setExpandedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
      const message = error.response?.data?.detail || 'Failed to update user';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`/admin/users/${userId}`);
      toast.success('✅ User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Please fill in name, email, and password');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/admin/users', {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        auth_provider: 'email'
      });
      toast.success('✅ User created successfully');
      setNewUser({ name: '', email: '', password: '', role: 'customer' });
      fetchUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      const message = error.response?.data?.detail || 'Failed to create user';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Password generator function
  const generatePassword = () => {
    const length = 16;
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const allChars = uppercase + lowercase + numbers + special;
    
    let password = '';
    // Ensure at least one of each type
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];
    
    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setNewUser(prev => ({ ...prev, password }));
    toast.success('🔐 Password generated!');
  };

  useEffect(() => {
    fetchUsers();
    fetchRoleSettings();
  }, []);

  const fetchRoleSettings = async () => {
    try {
      const response = await axios.get('/admin/settings');
      setRoleSettings({
        new_user_default_role: response.data.default_user_role || 'customer'
      });
    } catch (error) {
      console.error('Failed to fetch role settings:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="text-center flex-1">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">User Management</h2>
          <p className="text-gray-600">Manage user accounts, roles, and permissions</p>
        </div>
        <Button
          onClick={() => setShowRolesDialog(true)}
          variant="outline"
          className="flex items-center gap-2"
          data-testid="user-roles-button"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          User Roles
        </Button>
      </div>

      {/* Add New User */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New User</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <input
            type="text"
            placeholder="Full Name"
            value={newUser.name}
            onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="email"
            placeholder="Email Address"
            value={newUser.email}
            onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          />
          <div className="relative">
            <input
              type="password"
              placeholder="Password"
              value={newUser.password}
              onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={generatePassword}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-800 text-sm font-medium"
              title="Generate strong password"
            >
              🔑
            </button>
          </div>
          <select
            value={newUser.role}
            onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          >
            <option value="customer">Customer</option>
            <option value="photographer">Photographer</option>
            <option value="admin">Admin</option>
          </select>
          <Button 
            onClick={handleAddUser}
            disabled={loading}
            className="bg-custom-orange hover:bg-custom-orange"
          >
            {loading ? 'Adding...' : 'Add User'}
          </Button>
        </div>
      </Card>

      {/* Users List */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">All Users ({filteredUsers.length})</h3>
        
        {/* Search and Filter */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="w-48">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="customer">Customer</option>
            </select>
          </div>
        </div>
        
        {filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchQuery || roleFilter !== 'all' ? 'No users match your search criteria' : 'No users found'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2">Name</th>
                  <th className="text-left py-3 px-2">Email</th>
                  <th className="text-left py-3 px-2">Role</th>
                  <th className="text-left py-3 px-2">Orders</th>
                  <th className="text-left py-3 px-2">Total Spend</th>
                  <th className="text-left py-3 px-2">Joined</th>
                  <th className="text-left py-3 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <React.Fragment key={user.id}>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      {/* View mode */}
                      <td className="py-3 px-2 font-medium">{user.name}</td>
                      <td className="py-3 px-2">{user.email}</td>
                      <td className="py-3 px-2">
                        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                          {user.role}
                        </Badge>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <Badge variant="secondary">{user.order_count || 0}</Badge>
                      </td>
                      <td className="py-3 px-2 font-medium text-green-700">
                        ${((user.total_spend || 0) / 100).toFixed(2)}
                      </td>
                      <td className="py-3 px-2 text-gray-600">
                        {formatDate ? formatDate(user.created_at, siteTimezone) : new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-2 space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            startEditingUser(user);
                            setExpandedUser(user.id);
                          }}
                          disabled={loading}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={loading}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                    
                    {/* Expandable Details Row */}
                    {expandedUser === user.id && (
                      <tr>
                        <td colSpan="7" className="bg-gray-50 p-6">
                          <div className="space-y-6 max-w-4xl">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">Edit User Details</h4>
                            
                            {/* Basic Info */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 rounded-lg border">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input
                                  type="text"
                                  value={editValues.name || ''}
                                  onChange={(e) => setEditValues(prev => ({ ...prev, name: e.target.value }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                  type="email"
                                  value={editValues.email || ''}
                                  onChange={(e) => setEditValues(prev => ({ ...prev, email: e.target.value }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                <input
                                  type="tel"
                                  value={editValues.phone || ''}
                                  onChange={(e) => setEditValues(prev => ({ ...prev, phone: e.target.value }))}
                                  placeholder="Phone number"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                <select
                                  value={editValues.role || 'customer'}
                                  onChange={(e) => setEditValues(prev => ({ ...prev, role: e.target.value }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                  <option value="customer">Customer</option>
                                  <option value="photographer">Photographer</option>
                                  <option value="admin">Admin</option>
                                </select>
                              </div>
                            </div>

                            {/* Billing Address */}
                            <div className="bg-white p-4 rounded-lg border">
                              <h5 className="font-semibold text-gray-900 mb-3">Billing Address</h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                                  <input
                                    type="text"
                                    value={editValues.billing_address?.street || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                      ...prev,
                                      billing_address: { ...prev.billing_address, street: e.target.value }
                                    }))}
                                    placeholder="123 Main Street"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                  <input
                                    type="text"
                                    value={editValues.billing_address?.city || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                      ...prev,
                                      billing_address: { ...prev.billing_address, city: e.target.value }
                                    }))}
                                    placeholder="Sydney"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                                  <input
                                    type="text"
                                    value={editValues.billing_address?.state || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                      ...prev,
                                      billing_address: { ...prev.billing_address, state: e.target.value }
                                    }))}
                                    placeholder="NSW"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                                  <input
                                    type="text"
                                    value={editValues.billing_address?.postcode || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                      ...prev,
                                      billing_address: { ...prev.billing_address, postcode: e.target.value }
                                    }))}
                                    placeholder="2000"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                                  <input
                                    type="text"
                                    value={editValues.billing_address?.country || 'Australia'}
                                    onChange={(e) => setEditValues(prev => ({
                                      ...prev,
                                      billing_address: { ...prev.billing_address, country: e.target.value }
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Shipping/Postal Address */}
                            <div className="bg-white p-4 rounded-lg border">
                              <div className="flex items-center justify-between mb-3">
                                <h5 className="font-semibold text-gray-900">Shipping Address</h5>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                  <input
                                    type="checkbox"
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        copyBillingToShipping();
                                      }
                                    }}
                                    className="w-4 h-4 text-blue-600 rounded"
                                  />
                                  <span className="text-gray-700">Same as Billing Address</span>
                                </label>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                                  <input
                                    type="text"
                                    value={editValues.postal_address?.street || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                      ...prev,
                                      postal_address: { ...prev.postal_address, street: e.target.value }
                                    }))}
                                    placeholder="123 Main Street"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                  <input
                                    type="text"
                                    value={editValues.postal_address?.city || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                      ...prev,
                                      postal_address: { ...prev.postal_address, city: e.target.value }
                                    }))}
                                    placeholder="Sydney"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                                  <input
                                    type="text"
                                    value={editValues.postal_address?.state || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                      ...prev,
                                      postal_address: { ...prev.postal_address, state: e.target.value }
                                    }))}
                                    placeholder="NSW"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                                  <input
                                    type="text"
                                    value={editValues.postal_address?.postcode || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                      ...prev,
                                      postal_address: { ...prev.postal_address, postcode: e.target.value }
                                    }))}
                                    placeholder="2000"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                                  <input
                                    type="text"
                                    value={editValues.postal_address?.country || 'Australia'}
                                    onChange={(e) => setEditValues(prev => ({
                                      ...prev,
                                      postal_address: { ...prev.postal_address, country: e.target.value }
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                              <Button 
                                onClick={() => handleUpdateUser(user.id)}
                                disabled={loading}
                                className="bg-custom-orange hover:bg-custom-orange text-white"
                              >
                                {loading ? 'Saving...' : 'Save All Changes'}
                              </Button>
                              <Button 
                                variant="outline"
                                onClick={() => {
                                  setExpandedUser(null);
                                  setEditValues({});
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* User Roles Dialog */}
      <Dialog open={showRolesDialog} onOpenChange={setShowRolesDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Roles Management</DialogTitle>
            <DialogDescription>
              Manage user roles and their associated discount pricing
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Default Role Setting */}
            <Card className="p-4">
              <h3 className="text-md font-semibold text-gray-900 mb-3">Default Role for New Users</h3>
              <select
                value={roleSettings.new_user_default_role || 'customer'}
                onChange={async (e) => {
                  const newRole = e.target.value;
                  try {
                    await axios.put('/admin/settings', { default_user_role: newRole });
                    setRoleSettings({ new_user_default_role: newRole });
                    toast.success(`✅ Default role set to: ${newRole}`);
                  } catch (error) {
                    console.error('Failed to save role:', error);
                    toast.error('Failed to save role settings');
                  }
                }}
                className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="customer">Customer</option>
                <option value="photographer">Photographer</option>
                <option value="admin">Admin</option>
              </select>
            </Card>

            {/* Roles Table */}
            <Card className="p-4">
              <h3 className="text-md font-semibold text-gray-900 mb-3">User Roles & Pricing</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4">Role</th>
                      <th className="text-left py-3 px-4">Description</th>
                      <th className="text-left py-3 px-4">Permissions</th>
                      <th className="text-left py-3 px-4">Discount %</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4">
                        <Badge variant="secondary">Customer</Badge>
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        Standard user account
                      </td>
                      <td className="py-3 px-4 text-gray-600 text-xs">
                        Upload images, Place orders, View own orders
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-gray-600">0%</span>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4">
                        <Badge variant="default" className="bg-purple-600">Photographer</Badge>
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        Professional photographer account
                      </td>
                      <td className="py-3 px-4 text-gray-600 text-xs">
                        All customer permissions, Bulk uploads, Priority support
                      </td>
                      <td className="py-3 px-4">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          placeholder="10"
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <span className="text-xs text-gray-500 ml-1">%</span>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4">
                        <Badge variant="default" className="bg-green-600">Admin</Badge>
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        Full administrative access
                      </td>
                      <td className="py-3 px-4 text-gray-600 text-xs">
                        All permissions, User management, Settings, Reports
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-gray-600">N/A</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Discount pricing functionality will be implemented in a future update. Currently, all users pay standard pricing regardless of role.
                </p>
              </div>
            </Card>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowRolesDialog(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
    </div>
  );
}

export default UserManagement;
